if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();

        this.header = document.querySelector('.header-wrapper');

        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          returnInput: this.querySelector('input[name="return_to"]'),
          button: this.querySelector('button.localization-form__select'),
          drawer: this.querySelector('.selector__dropdown[element-s-up]'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };

        this.initializeForm();
      }

      initializeForm() {
        if (!theme.config.isTouch || Shopify.designMode) {
          Motion.inView(this, this.init.bind(this));
        } else {
          theme.initWhenVisible(this.init.bind(this));
        }
      }

      init() {
        this.addEventListener(
          'keyup',
          this.onContainerKeyUp.bind(this)
        );

        this.addEventListener(
          'keydown',
          this.onContainerKeyDown.bind(this)
        );

        if (theme.config.mql) {
          this.addEventListener(
            'focusout',
            this.closeSelector.bind(this)
          );
        }

        this.querySelector('#Selector-Overlay')?.addEventListener(
          'click',
          this.hidePanel.bind(this)
        );

        this.elements.button?.addEventListener(
          'click',
          this.openSelector.bind(this)
        );

        if (
          theme.config.mqlDesktop &&
          this.getAttribute('activate-event') === 'hover'
        ) {
          const hoverEvents = [
            'focusin',
            'mouseenter',
            'mouseleave',
          ];

          hoverEvents.forEach((event) =>
            this.addEventListener(
              event,
              this._hoverOpen.bind(this)
            )
          );
        }

        if (this.elements.search) {
          this.elements.search.addEventListener(
            'keyup',
            this.filterCountries.bind(this)
          );

          this.elements.search.addEventListener(
            'focus',
            this.onSearchFocus.bind(this)
          );

          this.elements.search.addEventListener(
            'blur',
            this.onSearchBlur.bind(this)
          );

          this.elements.search.addEventListener(
            'keydown',
            this.onSearchKeyDown.bind(this)
          );
        }

        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener(
            'click',
            this.hidePanel.bind(this)
          );
        }

        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener(
            'click',
            this.resetFilter.bind(this)
          );

          this.elements.resetButton.addEventListener(
            'mousedown',
            (event) => event.preventDefault()
          );
        }

        /*
         * Country-only localization can be controlled by
         * <details> instead of localization-form__select.
         *
         * Load the country template only when that dropdown
         * is actually opened.
         */
        const countryDetails = this.querySelector(
          'details.details--dropdown-language'
        );

        if (
          countryDetails &&
          this.querySelector('.countries-lazy-template')
        ) {
          countryDetails.addEventListener(
            'toggle',
            () => {
              if (countryDetails.open) {
                this.loadCountries();
              }
            }
          );
        }

        this.bindCountryLinks(this);
      }

      /*
       * Inserts the country lists from <template> into
       * the live DOM only when the selector is used.
       */
      loadCountries() {
        const template = this.querySelector(
          '.countries-lazy-template'
        );

        const container = this.querySelector(
          '.countries-lazy-container'
        );

        if (
          !template ||
          !container ||
          container.dataset.loaded === 'true'
        ) {
          return;
        }

        const templateContent =
          template.content.cloneNode(true);

        container.appendChild(
          templateContent
        );

        container.dataset.loaded = 'true';

        template.remove();

        this.bindCountryLinks(container);
      }

      /*
       * Bind click events only to actual localization links.
       */
      bindCountryLinks(scope) {
        scope
          .querySelectorAll('a[data-value]')
          .forEach((item) => {
            if (
              item.dataset.localizationBound === 'true'
            ) {
              return;
            }

            item.addEventListener(
              'click',
              this.onItemClick.bind(this)
            );

            item.dataset.localizationBound = 'true';
          });
      }

      hidePanel() {
        this.elements.button?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.elements.drawer?.classList.remove(
          'active'
        );

        if (this.elements.search) {
          this.elements.search.value = '';

          if (
            this.querySelector(
              '.countries-lazy-container'
            )?.dataset.loaded === 'true'
          ) {
            this.filterCountries();
          }

          this.elements.search.setAttribute(
            'aria-activedescendant',
            ''
          );
        }

        if (!theme.config.mql) {
          removeTrapFocus(
            this.elements.button
          );
        }

        document.body.classList.remove(
          'overflow-hidden-mobile'
        );

        document
          .querySelector('.menu-drawer')
          ?.classList.remove(
            'disclosure-selector-open'
          );

        if (this.header) {
          this.header.preventHide = false;
        }
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(
          this.querySelectorAll(
            'a[data-value]'
          )
        ).filter(
          (item) =>
            !item.parentElement.classList.contains(
              'hidden'
            )
        );

        if (!focusableItems.length) {
          return;
        }

        let focusedItemIndex =
          focusableItems.findIndex(
            (item) =>
              item === document.activeElement
          );

        let itemToFocus;

        switch (
          event.code.toUpperCase()
        ) {
          case 'ARROWUP':
            event.preventDefault();

            itemToFocus =
              focusedItemIndex > 0
                ? focusableItems[
                    focusedItemIndex - 1
                  ]
                : focusableItems[
                    focusableItems.length - 1
                  ];

            itemToFocus?.focus();
            break;

          case 'ARROWDOWN':
            event.preventDefault();

            itemToFocus =
              focusedItemIndex <
              focusableItems.length - 1
                ? focusableItems[
                    focusedItemIndex + 1
                  ]
                : focusableItems[0];

            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) {
          return;
        }

        setTimeout(() => {
          focusedItemIndex =
            focusableItems.findIndex(
              (item) =>
                item === document.activeElement
            );

          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute(
              'aria-activedescendant',
              focusableItems[
                focusedItemIndex
              ].id
            );
          } else {
            this.elements.search.setAttribute(
              'aria-activedescendant',
              ''
            );
          }
        });
      }

      onContainerKeyUp(event) {
        switch (
          event.code.toUpperCase()
        ) {
          case 'ESCAPE':
            if (
              this.elements.button &&
              this.elements.button.getAttribute(
                'aria-expanded'
              ) === 'false'
            ) {
              return;
            }

            this.hidePanel();

            event.stopPropagation();

            this.elements.button?.focus();
            break;

          case 'SPACE':
            if (!this.elements.button) {
              return;
            }

            if (
              this.elements.button.getAttribute(
                'aria-expanded'
              ) === 'true'
            ) {
              return;
            }

            event.preventDefault();

            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();

        const form =
          this.querySelector('form');

        if (
          !this.elements.input ||
          !event.currentTarget.dataset.value
        ) {
          return;
        }

        this.elements.input.value =
          event.currentTarget.dataset.value;

        if (this.elements.returnInput) {
          this.elements.returnInput.value =
            window.location.href;
        }

        if (form) {
          form.submit();
        }
      }

      openSelector() {
        /*
         * Load countries BEFORE opening.
         */
        this.loadCountries();

        this.elements.button?.focus();

        this.elements.drawer?.classList.toggle(
          'active'
        );

        if (this.elements.button) {
          this.elements.button.setAttribute(
            'aria-expanded',
            (
              this.elements.button.getAttribute(
                'aria-expanded'
              ) === 'false'
            ).toString()
          );
        }

        if (
          !document.body.classList.contains(
            'overflow-hidden-tablet'
          )
        ) {
          document.body.classList.add(
            'overflow-hidden-mobile'
          );
        }

        if (theme.config.mql) {
          if (this.elements.search) {
            this.elements.search.focus();
          }
        } else if (this.elements.drawer) {
          this.addEventListener(
            'transitionend',
            () => {
              const containerToTrapFocusOn =
                this.querySelector(
                  '.slide__inner--mb'
                );

              const focusElement =
                this.querySelector(
                  '.selector__close-button'
                );

              if (
                containerToTrapFocusOn &&
                focusElement
              ) {
                trapFocus(
                  containerToTrapFocusOn,
                  focusElement
                );
              }
            },
            {
              once: true,
            }
          );

          this.elements.drawer.classList.add(
            'drawer-slide-mb'
          );
        }

        if (
          this.hasAttribute(
            'data-prevent-hide'
          ) &&
          this.header
        ) {
          this.header.preventHide = true;
        }

        document
          .querySelector('.menu-drawer')
          ?.classList.add(
            'disclosure-selector-open'
          );
      }

      _hoverOpen(event) {
        const value =
          event.type === 'mouseenter' ||
          event.type === 'focusin';

        if (value) {
          this.loadCountries();
        }

        this.elements.drawer?.classList.toggle(
          'active',
          value
        );

        this.elements.button?.setAttribute(
          'aria-expanded',
          value.toString()
        );

        if (
          !document.body.classList.contains(
            'overflow-hidden-tablet'
          )
        ) {
          document.body.classList.toggle(
            'overflow-hidden-mobile',
            value
          );
        }

        if (
          this.hasAttribute(
            'data-prevent-hide'
          ) &&
          this.header
        ) {
          this.header.preventHide = value;
        }

        document
          .querySelector('.menu-drawer')
          ?.classList.toggle(
            'disclosure-selector-open',
            value
          );
      }

      closeSelector(event) {
        if (
          !this.contains(event.target) ||
          !this.contains(
            event.relatedTarget
          )
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(
            /\p{Diacritic}/gu,
            ''
          )
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) {
          return;
        }

        /*
         * If the user focuses/searches before
         * countries were loaded, load them now.
         */
        this.loadCountries();

        const searchValue =
          this.normalizeString(
            this.elements.search.value
          );

        const popularCountries =
          this.querySelector(
            '.popular-countries'
          );

        const countryLinks =
          this.querySelectorAll(
            'a[data-value] .country'
          );

        let visibleCountries =
          countryLinks.length;

        this.elements.resetButton?.classList.toggle(
          'hidden',
          !searchValue
        );

        if (popularCountries) {
          popularCountries.classList.toggle(
            'hidden',
            Boolean(searchValue)
          );
        }

        countryLinks.forEach(
          (countryElement) => {
            const link =
              countryElement.closest('a');

            const countryName =
              this.normalizeString(
                countryElement.textContent
              );

            if (
              countryName.indexOf(
                searchValue
              ) > -1
            ) {
              link?.parentElement?.classList.remove(
                'hidden'
              );
            } else {
              link?.parentElement?.classList.add(
                'hidden'
              );

              visibleCountries--;
            }
          }
        );

        if (
          this.elements.liveRegion &&
          window.accessibilityStrings
            ?.countrySelectorSearchCount
        ) {
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.countrySelectorSearchCount.replace(
              '[count]',
              visibleCountries
            );
        }

        const countrySelector =
          this.querySelector(
            '.country-selector'
          );

        const countryList =
          this.querySelector(
            '.country-selector__list'
          );

        if (countrySelector) {
          countrySelector.scrollTop = 0;
        }

        if (countryList) {
          countryList.scrollTop = 0;
        }
      }

      resetFilter(event) {
        event.stopPropagation();

        if (!this.elements.search) {
          return;
        }

        this.elements.search.value = '';

        this.filterCountries();

        this.elements.search.focus();
      }

      onSearchFocus() {
        this.loadCountries();

        this.elements.searchIcon?.classList.add(
          'country-filter__search-icon--hidden'
        );
      }

      onSearchBlur() {
        if (
          this.elements.search &&
          !this.elements.search.value
        ) {
          this.elements.searchIcon?.classList.remove(
            'country-filter__search-icon--hidden'
          );
        }
      }

      onSearchKeyDown(event) {
        if (
          event.code.toUpperCase() ===
          'ENTER'
        ) {
          event.preventDefault();
        }
      }
    }
  );
}


/*
 * DESKTOP / DROPDOWN LOCALIZATION
 */
if (
  !customElements.get(
    'dropdown-localization-component'
  )
) {
  customElements.define(
    'dropdown-localization-component',

    class DropdownLocalizationComponent extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.waitForSectionFetcher();
      }

      waitForSectionFetcher() {
        const sectionFetcher =
          this.querySelector(
            'section-fetcher'
          );

        if (!sectionFetcher) {
          this.initializeForm();
          return;
        }

        let completedFetchers = 0;

        const totalFetchers = 1;

        const onSectionCached = () => {
          completedFetchers++;

          if (
            completedFetchers >=
            totalFetchers
          ) {
            document.removeEventListener(
              'section:cached',
              onSectionCached
            );

            this.initializeForm();
          }
        };

        document.addEventListener(
          'section:cached',
          onSectionCached
        );

        const targetElement =
          document.getElementById(
            sectionFetcher.dataset.targetId
          );

        if (
          targetElement &&
          targetElement.hasAttribute(
            'data-loaded'
          )
        ) {
          completedFetchers++;
        }

        if (
          completedFetchers >=
          totalFetchers
        ) {
          document.removeEventListener(
            'section:cached',
            onSectionCached
          );

          this.initializeForm();
        }
      }

      initializeForm() {
        if (
          !theme.config.isTouch ||
          Shopify.designMode
        ) {
          Motion.inView(
            this,
            this.init.bind(this)
          );
        } else {
          theme.initWhenVisible(
            this,
            this.init.bind(this)
          );
        }
      }

      init() {
        this.header =
          document.querySelector(
            '.header-wrapper'
          );

        this.elements = {
          button: this.querySelector(
            '.dropdown-localization__button'
          ),

          drawer: this.querySelector(
            '.selector__dropdown[element-s-up]'
          ),

          panel: this.querySelector(
            '.disclosure__list-wrapper'
          ),
        };

        this.addEventListener(
          'keyup',
          this.onContainerKeyUp.bind(this)
        );

        if (
          theme.config.mql?.matches
        ) {
          this.addEventListener(
            'focusout',
            this.closeSelector.bind(this)
          );
        }

        this.elements.button?.addEventListener(
          'click',
          this.openSelector.bind(this)
        );
      }

      /*
       * Load any country template inside this
       * desktop localization component.
       */
      loadCountries() {
        this.querySelectorAll(
          'localization-form'
        ).forEach((form) => {
          if (
            typeof form.loadCountries ===
            'function'
          ) {
            form.loadCountries();
          }
        });
      }

      onContainerKeyUp(event) {
        switch (
          event.code.toUpperCase()
        ) {
          case 'ESCAPE':
            if (
              this.elements.button?.getAttribute(
                'aria-expanded'
              ) === 'false'
            ) {
              return;
            }

            this.hidePanel();

            event.stopPropagation();

            this.elements.button?.focus();
            break;

          case 'SPACE':
            if (
              this.elements.button?.getAttribute(
                'aria-expanded'
              ) === 'true'
            ) {
              return;
            }

            event.preventDefault();

            this.openSelector();
            break;
        }
      }

      openSelector() {
        /*
         * Countries only enter the DOM now.
         */
        this.loadCountries();

        this.elements.button?.focus();

        this.elements.drawer?.classList.toggle(
          'active'
        );

        if (this.elements.button) {
          this.elements.button.setAttribute(
            'aria-expanded',
            (
              this.elements.button.getAttribute(
                'aria-expanded'
              ) === 'false'
            ).toString()
          );
        }

        if (
          theme.config.mql &&
          this.elements.search
        ) {
          this.elements.search.focus();
        } else if (
          this.elements.drawer
        ) {
          this.addEventListener(
            'transitionend',
            () => {
              const containerToTrapFocusOn =
                this.querySelector(
                  '.slide__inner--mb'
                );

              const focusElement =
                this.querySelector(
                  '.selector__close-button'
                );

              if (
                containerToTrapFocusOn &&
                focusElement
              ) {
                trapFocus(
                  containerToTrapFocusOn,
                  focusElement
                );
              }
            },
            {
              once: true,
            }
          );

          this.elements.drawer.classList.add(
            'drawer-slide-mb'
          );
        }

        if (
          this.hasAttribute(
            'data-prevent-hide'
          ) &&
          this.header
        ) {
          this.header.preventHide = true;
        }

        document
          .querySelector('.menu-drawer')
          ?.classList.add(
            'disclosure-selector-open'
          );
      }

      closeSelector(event) {
        event.preventDefault();

        if (
          !this.contains(event.target) ||
          !this.contains(
            event.relatedTarget
          )
        ) {
          this.hidePanel();
        }
      }

      hidePanel() {
        this.elements.button?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.elements.drawer?.classList.remove(
          'active'
        );

        document
          .querySelector('.menu-drawer')
          ?.classList.remove(
            'disclosure-selector-open'
          );

        if (this.header) {
          this.header.preventHide = false;
        }
      }
    }
  );
}


/*
 * MOBILE / DRAWER LOCALIZATION
 */
if (
  !customElements.get(
    'drawer-localization-component'
  )
) {
  customElements.define(
    'drawer-localization-component',

    class DrawerLocalizationComponent extends HTMLElement {
      constructor() {
        super();

        this.waitForSectionFetcher();
      }

      waitForSectionFetcher() {
        const sectionFetcher =
          this.querySelector(
            'section-fetcher'
          );

        if (!sectionFetcher) {
          this.initializeForm();
          return;
        }

        let completedFetchers = 0;

        const totalFetchers = 1;

        const onSectionCached = () => {
          completedFetchers++;

          if (
            completedFetchers >=
            totalFetchers
          ) {
            document.removeEventListener(
              'section:cached',
              onSectionCached
            );

            this.initializeForm();
          }
        };

        document.addEventListener(
          'section:cached',
          onSectionCached
        );

        const targetElement =
          document.getElementById(
            sectionFetcher.dataset.targetId
          );

        if (
          targetElement &&
          targetElement.hasAttribute(
            'data-loaded'
          )
        ) {
          completedFetchers++;
        }

        if (
          completedFetchers >=
          totalFetchers
        ) {
          document.removeEventListener(
            'section:cached',
            onSectionCached
          );

          this.initializeForm();
        }
      }

      initializeForm() {
        if (
          !theme.config.isTouch ||
          Shopify.designMode
        ) {
          Motion.inView(
            this,
            this.init.bind(this)
          );
        } else {
          theme.initWhenVisible(
            this,
            this.init.bind(this)
          );
        }
      }

      init() {
        /*
         * IMPORTANT:
         * Only load templates that are direct children
         * of drawer-localization-component.
         *
         * Never automatically clone
         * .countries-lazy-template here.
         */
        this.loadTemplate();

        this.header =
          document.querySelector(
            '.header-wrapper'
          );

        this.elements = {
          button: this.querySelector(
            '.drawer-localization__button'
          ),

          drawer: this.querySelector(
            '.selector__dropdown[element-s-up]'
          ),

          backButton: this.querySelector(
            '.menu-drawer__back-button'
          ),
        };

        this.addEventListener(
          'keyup',
          this.onContainerKeyUp.bind(this)
        );

        this.elements.button?.addEventListener(
          'click',
          this.openSelector.bind(this)
        );

        this.elements.backButton?.addEventListener(
          'click',
          this.closeSelector.bind(this)
        );
      }

      /*
       * Preserve the theme's normal template loader,
       * but do NOT let it accidentally clone the
       * country lazy template.
       */
      loadTemplate() {
        const template =
          this.querySelector(
            ':scope > template:not(.countries-lazy-template)'
          );

        if (!template) {
          return;
        }

        const templateContent =
          template.content.cloneNode(true);

        this.appendChild(
          templateContent
        );
      }

      /*
       * Load country lists inside nested
       * localization-form components only
       * when the drawer is opened.
       */
      loadCountries() {
        this.querySelectorAll(
          'localization-form'
        ).forEach((form) => {
          if (
            typeof form.loadCountries ===
            'function'
          ) {
            form.loadCountries();
          }
        });
      }

      openSelector() {
        /*
         * Countries enter the live DOM here,
         * not during initial page load.
         */
        this.loadCountries();

        this.elements.button?.focus();

        this.elements.drawer?.classList.add(
          'active'
        );

        this.elements.button?.setAttribute(
          'aria-expanded',
          'true'
        );
      }

      onContainerKeyUp(event) {
        switch (
          event.code.toUpperCase()
        ) {
          case 'ESCAPE':
            if (
              this.elements.button?.getAttribute(
                'aria-expanded'
              ) === 'false'
            ) {
              return;
            }

            this.hidePanel();

            event.stopPropagation();

            this.elements.button?.focus();
            break;

          case 'SPACE':
            if (
              this.elements.button?.getAttribute(
                'aria-expanded'
              ) === 'true'
            ) {
              return;
            }

            event.preventDefault();

            this.openSelector();
            break;
        }
      }

      closeSelector(event) {
        event?.preventDefault();

        this.hidePanel();
      }

      hidePanel() {
        this.elements.button?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.elements.drawer?.classList.remove(
          'active'
        );

        document.body.classList.remove(
          'overflow-hidden-mobile'
        );

        document
          .querySelector('.menu-drawer')
          ?.classList.remove(
            'disclosure-selector-open'
          );

        if (this.header) {
          this.header.preventHide = false;
        }
      }
    }
  );
}